import { ChevronLeftIcon } from '@ordr-as/icons'
import { LoadingWand } from '@ordr-as/umami-react'
import DetailPlaceholder from 'components/DetailPlaceholder'
import Tab from 'components/Tab/Tab'
import TabContent from 'components/Tab/TabContent'
import TabList from 'components/Tab/TabList'
import {
    usePaymentMethodCountries,
    usePaymentMethodDetails,
    usePaymentMethodLocations,
} from 'hooks/usePaymentMethodApi'
import { useNavigate, Routes, Route, Navigate, useParams, Link } from 'react-router-dom'
import { PaymentProvider } from '../../../generated-api'
import PaymentMethodDetails from './PaymentMethodDetails'
import PaymentMethodCountryConnectionDetails from 'components/PaymentMethodCountryConnectionDetails/PaymentMethodCountryConnectionDetails'
import PaymentMethodLocationConnectionDetails from 'components/PaymentMethodLocationConnectionDetails/PaymentMethodLocationConnectionDetails'
import PaymentMethodCountryList from 'components/PaymentMethodCountryList/PaymentMethodCountryList'
import PaymentMethodLocationList from 'components/PaymentMethodLocationList/PaymentMethodLocationList'

const PaymentMethodDetailsPage = () => {
    const { paymentProvider, paymentMethod } = useParams()

    if (!paymentProvider || !paymentMethod) {
        throw new Error('Payment provider and payment method must be present in path.')
    }

    const navigate = useNavigate()

    const { data: paymentMethodDetails, isSuccess: isPaymentMethodDetailsSuccess } = usePaymentMethodDetails(
        paymentProvider as PaymentProvider,
        paymentMethod
    )
    const { data: countries, isSuccess: isCountriesSuccess } = usePaymentMethodCountries(
        paymentProvider as PaymentProvider,
        paymentMethod
    )
    const { data: locations, isSuccess: isLocationsSuccess } = usePaymentMethodLocations(
        paymentProvider as PaymentProvider,
        paymentMethod
    )

    if (!isPaymentMethodDetailsSuccess) {
        return <LoadingWand className="mx-auto" />
    }

    const selectedTab = location.pathname.split('/')[4]

    return (
        <div className="flex h-main">
            <div className="h-full w-1/2 flex flex-col bg-brand-white border-r-[1px] border-grey-medium">
                <div className="px-8 pt-4 bg-grey-lightest border-b-[1px] border-grey-medium">
                    <Link to="/payment-methods" className="flex items-center text-[#3b82f6] text-sm cursor-pointer">
                        <ChevronLeftIcon className="mr-2" />
                        Back to list
                    </Link>

                    <div className="my-7">
                        <h1>
                            {paymentMethodDetails.paymentProvider} - {paymentMethodDetails.paymentMethod}
                        </h1>
                        <p>
                            On this page you can configure which countries and locations has access to this payment
                            method.
                        </p>
                    </div>

                    <TabList>
                        <Tab isActive={selectedTab === 'details'} onClick={() => navigate('details')}>
                            Details
                        </Tab>
                        <Tab isActive={selectedTab === 'countries'} onClick={() => navigate('countries')}>
                            Countries
                        </Tab>
                        <Tab isActive={selectedTab === 'locations'} onClick={() => navigate('locations')}>
                            Locations
                        </Tab>
                    </TabList>
                </div>

                <TabContent>
                    <Routes>
                        <Route
                            path="details/*"
                            element={<PaymentMethodDetails paymentMethodDetails={paymentMethodDetails} />}
                        />

                        <Route
                            path="countries/*"
                            element={
                                isCountriesSuccess ? (
                                    <PaymentMethodCountryList
                                        paymentProvider={paymentProvider as PaymentProvider}
                                        paymentMethod={paymentMethod}
                                        countries={countries}
                                    />
                                ) : (
                                    <LoadingWand className="mx-auto" />
                                )
                            }
                        />
                        <Route
                            path="locations/*"
                            element={
                                isLocationsSuccess ? (
                                    <PaymentMethodLocationList
                                        paymentProvider={paymentProvider as PaymentProvider}
                                        paymentMethod={paymentMethod}
                                        locations={locations}
                                    />
                                ) : (
                                    <LoadingWand className="mx-auto" />
                                )
                            }
                        />

                        {/** Set the first tab to be the default **/}
                        <Route path="*" element={<Navigate to="details" replace />} />
                    </Routes>
                </TabContent>
            </div>

            <div className="h-full w-1/2 bg-brand-white">
                <Routes>
                    <Route path="countries/*">
                        <Route
                            path=":countryCode"
                            element={<PaymentMethodCountryConnectionDetails onClose={() => navigate('countries')} />}
                        />
                        <Route path="*" element={<DetailPlaceholder description="Select a country to see details" />} />
                    </Route>

                    <Route path="locations/*">
                        <Route
                            path=":locationId"
                            element={
                                <PaymentMethodLocationConnectionDetails
                                    locations={locations ?? []}
                                    onClose={() => navigate('locations')}
                                />
                            }
                        />
                        <Route
                            path="*"
                            element={<DetailPlaceholder description="Select a location to see details" />}
                        />
                    </Route>
                </Routes>
            </div>
        </div>
    )
}

export default PaymentMethodDetailsPage
